import { useMemo } from "react";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { DesignPackageServices } from "@/services/bedrock/DesignPackageServices";
import { DesignPackageListParams, DesignPackageSummary } from "@/services/bedrock/dtos/DesignPackage";
import { Page } from "@/services/bedrock/dtos/Page";
import { designKeys } from "@/services/bedrock/queryKeys";

export const DESIGN_PACKAGES_PAGE_SIZE = 24;

export type DesignPackagesInfiniteParams = Omit<DesignPackageListParams, "page" | "cursor">;

export const getNextPageParam = (last: Page<DesignPackageSummary>): string | undefined =>
  last.pagination.hasMore ? last.pagination.nextCursor ?? undefined : undefined;

/** Cursor-paginated "My designs" grid, 24 per page. */
export function useDesignPackagesInfinite(params: DesignPackagesInfiniteParams, opts: { enabled?: boolean } = {}) {
  const query = useInfiniteQuery({
    queryKey: designKeys.listInfinite(params),
    queryFn: ({ pageParam }) =>
      DesignPackageServices.list({ ...params, perPage: DESIGN_PACKAGES_PAGE_SIZE, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    enabled: opts.enabled ?? true,
  });

  const packages = useMemo(() => query.data?.pages.flatMap(page => page.content) ?? [], [query.data?.pages]);
  const pagesLoaded = query.data?.pages.length ?? 0;

  return { ...query, packages, pagesLoaded };
}
